'use client'

import * as React from 'react'
import { Calendar, ChevronRight } from 'lucide-react'
import { questions, catMeta } from './types'

export interface PresenceHistoryResult {
  id: string
  type: string
  answers: Record<string, number>
  notes: string | null
  createdAt: string
}

export interface PresenceHistoryListProps {
  results: PresenceHistoryResult[]
  onResultClick: (id: string) => void
  emptyStateAction?: () => void
  emptyStateActionLabel?: string
}

function calculateScores(answers: Record<string, number>) {
  const catScores = {
    immersion: { total: 0, count: 0 },
    attunement: { total: 0, count: 0 },
    emotional: { total: 0, count: 0 },
    focus: { total: 0, count: 0 },
  }

  questions.forEach((q, i) => {
    const answer = answers[i.toString()]
    if (answer !== null && answer !== undefined) {
      const scoreVal = q.reverse ? 8 - answer : answer
      const catData = catScores[q.cat as keyof typeof catScores]
      if (catData) {
        catData.total += scoreVal
        catData.count += 1
      }
    }
  })

  const scores = {
    immersion: catScores.immersion.count > 0 ? catScores.immersion.total / catScores.immersion.count : 0,
    attunement: catScores.attunement.count > 0 ? catScores.attunement.total / catScores.attunement.count : 0,
    emotional: catScores.emotional.count > 0 ? catScores.emotional.total / catScores.emotional.count : 0,
    focus: catScores.focus.count > 0 ? catScores.focus.total / catScores.focus.count : 0,
  }

  const validScores = Object.values(scores).filter(s => s > 0)
  const overallScore = validScores.length > 0 ? validScores.reduce((a, b) => a + b, 0) / validScores.length : 0

  return { scores, overallScore }
}

function getScoreColor(score: number) {
  if (score >= 5.5) return '#4A7C7C'
  if (score >= 4) return '#D4A574'
  return '#C4938A'
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
   timeZone: 'America/Toronto'})
}

export function PresenceHistoryList({
  results,
  onResultClick,
  emptyStateAction,
  emptyStateActionLabel = 'Start Self-Reflection',
}: PresenceHistoryListProps) {
  if (results.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 shadow-[0_2px_20px_rgba(0,0,0,0.06)] text-center">
        <div className="w-16 h-16 bg-[#E8F2F2] rounded-2xl flex items-center justify-center text-3xl mx-auto mb-5">
          🧘
        </div>
        <h2 className="font-semibold text-[#2D2D2D] mb-2">No reflections yet</h2>
        <p className="text-[#7A7A7A] mb-6">
          Complete a Therapeutic Presence self-reflection to see your results here.
        </p>
        {emptyStateAction && (
          <button
            onClick={emptyStateAction}
            className="inline-flex px-6 py-3 rounded-full font-bold bg-[#4A7C7C] hover:bg-[#2E5B5B] text-white transition-all"
          >
            {emptyStateActionLabel} →
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {results.map((result) => {
        const { scores, overallScore } = calculateScores(result.answers)

        return (
          <button
            key={result.id}
            onClick={() => onResultClick(result.id)}
            className="block w-full text-left bg-white rounded-2xl p-6 shadow-[0_2px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_30px_rgba(0,0,0,0.1)] transition-all group"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 text-sm text-[#7A7A7A] mb-2">
                  <Calendar className="h-4 w-4" />
                  {formatDate(result.createdAt)}
                </div>
                {result.notes && (
                  <p className="text-sm text-[#7A7A7A] line-clamp-1 max-w-[280px]">
                    {result.notes}
                  </p>
                )}
              </div>
              <div className="text-right flex items-center gap-3">
                <div>
                  <div className="text-3xl font-serif" style={{ color: getScoreColor(overallScore) }}>
                    {overallScore.toFixed(1)}
                  </div>
                  <div className="text-xs text-[#7A7A7A]">overall score</div>
                </div>
                <ChevronRight className="h-5 w-5 text-[#9A9A9A] group-hover:text-[#4A7C7C] group-hover:translate-x-1 transition-all" />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {(['immersion', 'attunement', 'emotional', 'focus'] as const).map((cat) => {
                const score = scores[cat]
                const meta = catMeta[cat]
                const percent = (score / 7) * 100

                return (
                  <div key={cat} className="bg-[#F8F5F2] rounded-xl p-3">
                    <div className="flex items-center gap-1 mb-2">
                      <span className="text-xs">{meta?.emoji}</span>
                      <span className="text-[10px] font-medium text-[#7A7A7A] uppercase tracking-wide">
                        {meta?.name.split(' ')[0]}
                      </span>
                    </div>
                    <div className="h-1.5 bg-[#E8E0D8] rounded-full overflow-hidden mb-1">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${percent}%`, backgroundColor: getScoreColor(score) }}
                      />
                    </div>
                    <div className="text-sm font-semibold text-[#2D2D2D]">
                      {score.toFixed(1)}
                    </div>
                  </div>
                )
              })}
            </div>
          </button>
        )
      })}
    </div>
  )
}
